import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, PAYMENT_URL, scrollToHash } from "../lib/site";
import { Logo } from "./Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const home = pathname === "/";

  const linkHref = (hash) => (home ? hash : `/#${hash.slice(1)}`);
  const linkClick = (e, hash) => {
    if (home) scrollToHash(e, hash, () => setOpen(false));
    else setOpen(false);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#E9DFFC]/80 bg-white/85 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        {home ? (
          <a href="#overview" onClick={(e) => scrollToHash(e, "#overview")} aria-label="Digital Product Academy — home">
            <Logo />
          </a>
        ) : (
          <Link to="/" aria-label="Digital Product Academy — home">
            <Logo />
          </Link>
        )}

        <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.hash}
              href={linkHref(l.hash)}
              onClick={(e) => linkClick(e, l.hash)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-semibold text-[#55506A] transition-colors duration-200 hover:text-[#7C3AED]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PAYMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-btn"
            className="hidden items-center gap-2 rounded-full bg-[#171321] px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_20px_-6px_rgba(23,19,33,0.5)] transition-[transform,background-color] duration-200 hover:scale-[1.03] hover:bg-[#7C3AED] sm:inline-flex"
          >
            Join Now — ₹199 <ArrowRight size={15} strokeWidth={2.5} />
          </a>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E9DFFC] text-[#171321] lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#E9DFFC] bg-white lg:hidden"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.hash}
                  href={linkHref(l.hash)}
                  onClick={(e) => linkClick(e, l.hash)}
                  className="block rounded-xl px-4 py-3 text-[15px] font-semibold text-[#171321] transition-colors hover:bg-[#F6F1FF] hover:text-[#7C3AED]"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-cta"
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#171321] px-5 py-3.5 text-sm font-bold text-white"
              >
                Join Now — ₹199 <ArrowRight size={15} strokeWidth={2.5} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
